import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  TrendingUp, TrendingDown, DollarSign, Truck, MapPin, Award, AlertTriangle,
  Loader2, RefreshCw, Users,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: 'day',     label: 'Day' },
  { value: 'week',    label: 'Week' },
  { value: 'month',   label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year',    label: 'Year' },
];

const fmt$ = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const fmt2 = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Animates a numeric target from `from` -> `target` over `duration` ms using
// requestAnimationFrame + ease-out curve. Re-runs whenever `target` changes
// (e.g. period toggle) so KPIs feel alive instead of snapping.
const useCountUp = (target, { duration = 900, decimals = 0 } = {}) => {
  const [value, setValue] = useState(0);
  const valueRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const final = Number(target) || 0;
    const from = valueRef.current;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const next = from + (final - from) * eased;
      const rounded = decimals === 0 ? Math.round(next) : Number(next.toFixed(decimals));
      valueRef.current = rounded;
      setValue(rounded);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, decimals]);

  return value;
};

const AnimatedMoney = ({ target }) => {
  const v = useCountUp(target, { decimals: 0 });
  return <>${v.toLocaleString()}</>;
};

const AnimatedDecimal = ({ target, decimals = 2 }) => {
  const v = useCountUp(target, { decimals });
  return <>${v.toFixed(decimals)}</>;
};

const AnimatedPercent = ({ target }) => {
  const v = useCountUp(target, { decimals: 0 });
  return <>{v}%</>;
};

const KpiCard = ({ label, value, sub, accent, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`text-3xl font-bold mt-1 ${accent || 'text-slate-900'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

const GradePill = ({ grade }) => {
  const cls = {
    'A+': 'bg-emerald-600 text-white',
    'A':  'bg-emerald-500 text-white',
    'B':  'bg-amber-500 text-white',
    'C':  'bg-orange-500 text-white',
    'D':  'bg-red-600 text-white',
  }[grade] || 'bg-slate-300 text-slate-700';
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}>{grade || '—'}</span>;
};

const StrengthPill = ({ strength }) => {
  const cls = {
    strong:  'bg-emerald-100 text-emerald-700',
    average: 'bg-amber-100 text-amber-700',
    weak:    'bg-red-100 text-red-700',
    unknown: 'bg-slate-100 text-slate-600',
  }[strength] || 'bg-slate-100 text-slate-600';
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${cls}`}>{strength}</span>;
};

const Section = ({ title, icon: Icon, children, testid }) => (
  <section className="bg-white border border-slate-200 rounded-md p-5" data-testid={testid}>
    <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
      <Icon className="w-4 h-4 text-[#002FA7]" /> {title}
    </h2>
    {children}
  </section>
);

const FleetProfitDashboardPage = () => {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/profitability/dashboard?period=${p}`, { withCredentials: true });
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(period); }, [load, period]);

  const runBackfill = useCallback(async () => {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const { data: r } = await axios.post(`${API}/profitability/backfill`, {}, { withCredentials: true });
      toast.success(`Backfilled ${r.updated} loads (${r.skipped} skipped, ${r.examined} examined)`);
      await load(period);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  }, [backfilling, load, period]);

  if (loading || !data) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-5 pb-24 lg:pb-8" data-testid="fleet-profit-dashboard">
          <header className="mb-2">
            <div className="h-9 w-72 bg-slate-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-96 bg-slate-100 rounded animate-pulse" />
          </header>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-md p-4">
                <div className="h-3 w-24 bg-slate-200 rounded animate-pulse mb-3" />
                <div className="h-7 w-28 bg-slate-300 rounded animate-pulse mb-2" />
                <div className="h-3 w-32 bg-slate-100 rounded animate-pulse" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-md p-5">
                <div className="h-4 w-32 bg-slate-200 rounded animate-pulse mb-3" />
                {[1, 2, 3].map((j) => (
                  <div key={j} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-b-0">
                    <div className="h-3 w-6 bg-slate-100 rounded animate-pulse" />
                    <div className="flex-1 h-3 bg-slate-100 rounded animate-pulse" />
                    <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 flex items-center gap-2 mt-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Crunching your fleet numbers…
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const t = data.totals || {};
  const u = data.fleet_utilization || {};

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-5 pb-24 lg:pb-8" data-testid="fleet-profit-dashboard">
        <header className="flex flex-wrap items-start justify-between gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Fleet Profit Dashboard
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Real-time profitability across drivers, customers, lanes and the whole fleet — last {data.window_days} day{data.window_days === 1 ? '' : 's'}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="period-toggle">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  data-testid={`period-${p.value}`}
                  className={`px-3 py-1.5 text-xs font-semibold ${period === p.value ? 'bg-[#002FA7] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              onClick={runBackfill}
              disabled={backfilling}
              data-testid="backfill-btn"
              title="Re-compute profitability for any load missing metrics"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border border-slate-300 hover:border-slate-400 rounded-md text-slate-700 disabled:opacity-50"
            >
              {backfilling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Backfill
            </button>
          </div>
        </header>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total Revenue" value={<AnimatedMoney target={t.total_revenue} />} sub={`${t.load_count || 0} delivered loads`} testid="kpi-revenue" />
          <KpiCard label="Total Profit" value={<AnimatedMoney target={t.total_profit} />} accent={Number(t.total_profit) >= 0 ? 'text-emerald-700' : 'text-red-700'} sub={`${(t.total_miles || 0).toLocaleString()} miles`} testid="kpi-profit" />
          <KpiCard label="Fleet RPM" value={<AnimatedDecimal target={t.avg_rpm} decimals={2} />} sub={`CPM ${fmt2(t.avg_cpm)}`} testid="kpi-rpm" />
          <KpiCard label="Utilization" value={<AnimatedPercent target={u.delivered_pct} />} sub={`${u.loads_delivered || 0} of ${u.load_count_total || 0} loads delivered`} testid="kpi-utilization" />
        </div>

        {/* Drivers + Customers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section title="Top Drivers" icon={Truck} testid="section-top-drivers">
            {(data.top_drivers || []).length === 0 ? (
              <p className="text-xs text-slate-500">No driver activity in this window.</p>
            ) : (
              <ol className="space-y-1.5">
                {data.top_drivers.map((d, i) => (
                  <li key={d.driver_id} className="flex items-center gap-3 text-sm py-1.5 border-b border-slate-100 last:border-b-0" data-testid={`top-driver-${i}`}>
                    <span className="text-[11px] font-bold w-6 text-slate-400">#{i + 1}</span>
                    <span className="flex-1 font-medium text-slate-900 truncate">{d.name}</span>
                    <span className="text-xs text-slate-500 tabular-nums">{d.load_count} loads</span>
                    <span className="text-sm font-bold tabular-nums text-emerald-700 w-24 text-right">{fmt$(d.total_profit)}</span>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          <Section title="Top Customers" icon={Users} testid="section-top-customers">
            {(data.top_customers || []).length === 0 ? (
              <p className="text-xs text-slate-500">No customer history yet.</p>
            ) : (
              <ol className="space-y-1.5">
                {data.top_customers.map((c, i) => (
                  <li key={`${c.broker}-${i}`} className="flex items-center gap-3 text-sm py-1.5 border-b border-slate-100 last:border-b-0" data-testid={`top-customer-${i}`}>
                    <span className="text-[11px] font-bold w-6 text-slate-400">#{i + 1}</span>
                    <span className="flex-1 font-medium text-slate-900 truncate">{c.broker}</span>
                    <GradePill grade={c.grade} />
                    <span className="text-xs text-slate-500 tabular-nums">{c.load_count} loads</span>
                    <span className="text-sm font-bold tabular-nums text-emerald-700 w-20 text-right">{fmt$(c.avg_profit)}/ld</span>
                  </li>
                ))}
              </ol>
            )}
          </Section>
        </div>

        {/* Lanes — strong + weak */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section title="Top Lanes by Profit" icon={Award} testid="section-strong-lanes">
            {(data.top_lanes || []).length === 0 ? (
              <p className="text-xs text-slate-500">No lane history yet.</p>
            ) : (
              <ol className="space-y-1.5">
                {data.top_lanes.map((ln, i) => (
                  <li key={`${ln.lane}-${i}`} className="flex items-center gap-3 text-sm py-1.5 border-b border-slate-100 last:border-b-0" data-testid={`strong-lane-${i}`}>
                    <MapPin className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                    <span className="flex-1 font-medium text-slate-900 truncate">{ln.lane}</span>
                    <StrengthPill strength={ln.strength} />
                    <span className="text-xs text-slate-500 tabular-nums">{ln.load_count}</span>
                    <span className="text-sm font-bold tabular-nums text-emerald-700 w-20 text-right">{fmt$(ln.avg_profit)}</span>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          <Section title="Weak Lanes" icon={AlertTriangle} testid="section-weak-lanes">
            {(data.weak_lanes || []).length === 0 ? (
              <p className="text-xs text-slate-500">No weak lanes detected. Keep it up!</p>
            ) : (
              <ol className="space-y-1.5">
                {data.weak_lanes.map((ln, i) => (
                  <li key={`${ln.lane}-${i}`} className="flex items-center gap-3 text-sm py-1.5 border-b border-slate-100 last:border-b-0" data-testid={`weak-lane-${i}`}>
                    <TrendingDown className="w-3 h-3 text-red-500 flex-shrink-0" />
                    <span className="flex-1 font-medium text-slate-900 truncate">{ln.lane}</span>
                    <StrengthPill strength={ln.strength} />
                    <span className="text-xs text-slate-500 tabular-nums">{ln.load_count}</span>
                    <span className="text-sm font-bold tabular-nums text-red-700 w-20 text-right">{fmt$(ln.avg_profit)}</span>
                  </li>
                ))}
              </ol>
            )}
          </Section>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 flex items-start gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <DollarSign className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Numbers reflect loads whose <strong>profitability has been computed</strong>. If older loads show $0, click <strong>Backfill</strong> to populate metrics across your fleet history.
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FleetProfitDashboardPage;
